import React, { RefObject, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Icon } from '@seeqdev/qomponents';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { ButtonWithPopover } from '@/core/ButtonWithPopover.molecule';
import { ColumnAction, SubMenuItem } from '@/core/tableUtilities/tableUtilities.types';
import { TextHeaderMenuAction } from '@/tableBuilder/tableBuilder.constants';
import { ColumnMenuTile } from '@/core/tableUtilities/ColumnMenuTile';
import { CapsulesPanelColumnHeaderMenuActions } from '@/trend/panels/capsulesPanel/capsulesPanel.types';

type GenericColumnAction = ColumnAction<TextHeaderMenuAction | CapsulesPanelColumnHeaderMenuActions>;

interface TableColumnMenuProps {
  columnActions: GenericColumnAction[];
  columnKey: string;
  iconRef?: RefObject<HTMLDivElement>;
}

/**
 * A three-dot "kebab" menu, with a dropdown. menuActions are dropdown options that are passed in as arguments.
 */
export const TableColumnMenu: React.FunctionComponent<TableColumnMenuProps> = ({
  columnActions,
  columnKey,
  iconRef,
}) => {
  const [show, setShow] = useState<Record<string, boolean>>({});

  const updateState = (key: string, value: boolean) => {
    setShow(_.assign({}, show, { [key]: value }));
  };

  const { t } = useTranslation();
  const showDropdown = (key: string) => updateState(key, !show[key]);
  const hideDropdown = (key: string) => updateState(key, false);

  const renderNavDropdownItem = (menuEntry: GenericColumnAction | SubMenuItem, parentKey?: string) => {
    return (
      <NavDropdown.Item
        disabled={menuEntry.disabled}
        key={`dropdown_${menuEntry.translationKey}`}
        onClick={() => {
          menuEntry.action?.();
          if (!_.isUndefined(parentKey)) {
            hideDropdown(parentKey);
          }
        }}
        className="textPrimaryMenuItem"
        data-testid={`actions_${menuEntry.translationKey}`}>
        <ColumnMenuTile leading={<Icon icon={menuEntry.iconClass} extraClassNames="fa-fw" type="inherit" />}>
          <span className="forceFont">{t(menuEntry.translationKey)}</span>
        </ColumnMenuTile>
      </NavDropdown.Item>
    );
  };

  const renderSimpleMenuEntry = (menuEntry: GenericColumnAction | SubMenuItem, parentKey?: string) => {
    return !menuEntry.tooltip ? (
      renderNavDropdownItem(menuEntry, parentKey)
    ) : (
      <HoverTooltip key={`tooltipContainer_${menuEntry.translationKey}`} text={menuEntry.tooltip}>
        <div>
          {/* the tooltip does not appear for disabled entries if we do not have the div*/}
          {renderNavDropdownItem(menuEntry, parentKey)}
        </div>
      </HoverTooltip>
    );
  };

  const renderMenuEntry = (menuEntry: GenericColumnAction) => {
    return (
      <NavDropdown
        title={
          <ColumnMenuTile leading={<Icon icon={menuEntry.iconClass} extraClassNames="fa-fw" type="inherit" />}>
            <span className="forceFont">{t(menuEntry.translationKey)}</span>
          </ColumnMenuTile>
        }
        id={`dropdown_${menuEntry.translationKey}`}
        key={`dropdown_${menuEntry.translationKey}`}
        drop="right"
        className="textPrimaryMenuItem"
        data-testid={`actions_${menuEntry.translationKey}`}
        show={show[menuEntry.translationKey]}
        onMouseEnter={() => showDropdown(menuEntry.translationKey)}
        onMouseLeave={() => hideDropdown(menuEntry.translationKey)}>
        {_.map(menuEntry.subMenuItems, (submenuItem) => renderSimpleMenuEntry(submenuItem, menuEntry.translationKey))}
      </NavDropdown>
    );
  };

  const renderDropdownEntry = (menuEntry: GenericColumnAction) => {
    return menuEntry.subMenuItems && !menuEntry.disabled
      ? // workaround because the disable property of the NavDropdown does not work
        renderMenuEntry(menuEntry)
      : renderSimpleMenuEntry(menuEntry);
  };

  return (
    <>
      <ButtonWithPopover
        label={
          <div className="sq-icon-hover" ref={iconRef}>
            <Icon icon="fc-more" extraClassNames="fa-fw mt2" type="gray" testId="columnMoreActions" />
          </div>
        }
        popoverConfig={{
          id: `dropdown-${columnKey}`,
          placement: 'bottom',
          contentClassNames: 'tableColumnMenu',
          wrapperClassNames: 'tableColumnMenuWrapper',
        }}>
        {columnActions.map((action) => renderDropdownEntry(action))}
      </ButtonWithPopover>
    </>
  );
};
